import { useI18n } from "../i18n.js";
import { PANEL } from "../readiness/text.js";
import * as React from "react";
import type { AgentDraft } from "@nemo/agent-schema";
import { Api, ApiError, type LeadDetail } from "../api.js";
import type { FieldErrors } from "../draft.js";
import { Field, ListEditor, Section, TextInput, Toggle } from "./ui.js";

type Lead = NonNullable<AgentDraft["leadCapture"]>;
type LeadField = Lead["fields"][number];

const PRESETS: { key: string; label: string; type: LeadField["type"] }[] = [
  { key: "firstName", label: "First name", type: "text" },
  { key: "lastName", label: "Last name", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "phone" },
];

interface Props {
  form: AgentDraft;
  update: (fn: (f: AgentDraft) => AgentDraft) => void;
  errors: FieldErrors;
  /** Needed for "Send a test lead". Without them the button is not shown. */
  api?: Api;
  agentId?: string;
  /** Saves the draft now, so the test uses what is on screen. */
  saveNow?: () => Promise<unknown>;
}

const slug = (label: string) => {
  const words = label.toLowerCase().normalize("NFKD").replace(/[^a-z0-9 ]/g, " ").trim().split(/\s+/).filter(Boolean);
  const k = words.map((w, i) => (i ? w[0]?.toUpperCase() + w.slice(1) : w)).join("");
  return /^[a-z]/.test(k) ? k : k ? `f${k}` : "";
};

export function UserDataStep({ form, update, errors, api, agentId, saveNow }: Props) {
  const { lang } = useI18n();
  const lc: Lead = { enabled: false, fields: [], notifyEmails: [], ...(form.leadCapture ?? {}) } as Lead;
  const set = (patch: Partial<Lead>) => update((f) => ({ ...f, leadCapture: { ...lc, ...patch } }));
  const has = (key: string) => lc.fields.some((f) => f.key === key);
  const toggle = (p: (typeof PRESETS)[number], on: boolean) => set({ fields: on ? [...lc.fields, { key: p.key, label: p.label, type: p.type, required: p.key === "email" }] : lc.fields.filter((f) => f.key !== p.key) });
  const customs = lc.fields.filter((f) => !PRESETS.some((p) => p.key === f.key));
  const crm = lc.crm;
  const flow = lc.flow ?? {};
  const setFlow = (patch: Partial<NonNullable<Lead["flow"]>>) => set({ flow: { ...flow, ...patch } });
  const setAsk = (key: string, ask: string) => set({ fields: lc.fields.map((x) => (x.key === key ? { ...x, ask: ask || undefined } : x)) });
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= lc.fields.length) return;
    const next = [...lc.fields];
    [next[i], next[j]] = [next[j] as LeadField, next[i] as LeadField];
    set({ fields: next });
  };
  const hasEmail = lc.fields.some((f) => f.type === "email");

  return (
    <div className="step">
      <Section title="Collect visitor details" help="The agent asks for these details during the conversation, one at a time, when it makes sense. It is not a form.">
        <Toggle label="Collect contact details (lead capture)" checked={lc.enabled} onChange={(v) => set({ enabled: v })} />
        <p className="hint">{PANEL.help.leadCapture[lang]}</p>
        {lc.enabled && (
          <>
            <Field label="Details to ask for" error={errors["leadCapture.fields"]}>
              <ul className="rows">
                {PRESETS.map((p) => {
                  const f = lc.fields.find((x) => x.key === p.key);
                  return (
                    <li key={p.key} className="row between">
                      <label className="inline">
                        <input type="checkbox" checked={!!f} onChange={(e) => toggle(p, e.target.checked)} /> {p.label}
                      </label>
                      {f && (
                        <label className="inline">
                          <input type="checkbox" checked={f.required ?? false} onChange={(e) => set({ fields: lc.fields.map((x) => (x.key === p.key ? { ...x, required: e.target.checked } : x)) })} aria-label={`${p.label} is required`} /> Required
                        </label>
                      )}
                    </li>
                  );
                })}
                {customs.map((f) => (
                  <li key={f.key} className="row between">
                    <span>{f.label}</span>
                    <span className="row">
                      <label className="inline">
                        <input type="checkbox" checked={f.required ?? false} onChange={(e) => set({ fields: lc.fields.map((x) => (x.key === f.key ? { ...x, required: e.target.checked } : x)) })} aria-label={`${f.label} is required`} /> Required
                      </label>
                      <button className="btn ghost" onClick={() => set({ fields: lc.fields.filter((x) => x.key !== f.key) })} aria-label={`Remove ${f.label}`}>
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              <CustomField existing={lc.fields.map((f) => f.key)} onAdd={(f) => set({ fields: [...lc.fields, f] })} />
            </Field>
            {lc.fields.length > 0 && (
              <Field label="How the agent asks" help="The agent asks in this order, one detail at a time. Optional details can be skipped by the visitor. Leave the wording empty to let the agent choose.">
                <ol className="rows">
                  {lc.fields.map((f, i) => (
                    <li key={f.key} className="row between">
                      <label className="inline grow">
                        <span>{f.label}{f.required ? "" : " (optional)"}</span>
                        <input aria-label={`How to ask for ${f.label}`} placeholder="e.g. Ask for the work email" maxLength={300} value={f.ask ?? ""} onChange={(e) => setAsk(f.key, e.target.value)} />
                      </label>
                      <span className="row">
                        <button className="btn ghost" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${f.label} up`}>
                          ↑
                        </button>
                        <button className="btn ghost" onClick={() => move(i, 1)} disabled={i === lc.fields.length - 1} aria-label={`Move ${f.label} down`}>
                          ↓
                        </button>
                      </span>
                    </li>
                  ))}
                </ol>
              </Field>
            )}
            <TextInput label="When should the agent ask?" multiline rows={2} max={500} value={flow.trigger ?? ""} onChange={(v) => setFlow({ trigger: v || undefined })} placeholder="e.g. When the visitor asks for a quote or a demo" help="Leave empty and the agent asks when a visitor shows interest in a quote, a service, a purchase or being contacted." />
            <TextInput label="What to say after saving" multiline rows={2} max={500} value={flow.confirmation ?? ""} onChange={(v) => setFlow({ confirmation: v || undefined })} placeholder="e.g. Thanks! Someone from our team will write to you within one business day." />
            <TextInput label="If the visitor does not want to share" multiline rows={2} max={500} value={flow.onRefusal ?? ""} onChange={(v) => setFlow({ onRefusal: v || undefined })} placeholder="e.g. No problem. You can also call us on 555 0100." help="Leave empty and the agent accepts politely, does not insist and keeps helping." />
            <ListEditor label="Notification emails" items={lc.notifyEmails} onChange={(v) => set({ notifyEmails: v })} type="email" placeholder="sales@example.com" addLabel="Add" errors={errors} errorPrefix="leadCapture.notifyEmails" help="We email these addresses each time someone shares their details." />
            <Field label="Send leads to a CRM" htmlFor="crm-type" help="Optional. Leads are always saved and emailed.">
              <select id="crm-type" value={crm?.type ?? ""} onChange={(e) => set({ crm: e.target.value ? ({ type: e.target.value, ...(e.target.value === "zapier" ? { webhookUrl: "" } : e.target.value === "wix" ? { siteId: "" } : { connectionId: "" }) } as Lead["crm"]) : undefined })}>
                <option value="">None</option>
                <option value="zapier">Zapier webhook</option>
                {/* Only what is delivered today is offered. An agent saved earlier with a destination that is not built yet keeps it
                    visible, marked, so nobody is surprised that leads do not arrive there. */}
                {crm?.type === "wix" && <option value="wix" disabled>Wix (not available yet)</option>}
                {crm?.type === "hubspot" && <option value="hubspot" disabled>HubSpot (not available yet)</option>}
              </select>
            </Field>
            {(crm?.type === "wix" || crm?.type === "hubspot") && (
              <p className="field-error" role="alert">
                {crm.type === "wix" ? "Wix" : "HubSpot"} is not connected yet, so leads are not sent there. They are still saved and e-mailed. Choose None or Zapier webhook.
              </p>
            )}
            {crm?.type === "zapier" && (
              <>
                <TextInput label="Zapier webhook URL" type="url" value={crm.webhookUrl} onChange={(v) => set({ crm: { ...crm, type: "zapier", webhookUrl: v } })} error={errors["leadCapture.crm.webhookUrl"]} placeholder="https://hooks.zapier.com/…" />
                <TextInput label="Signing secret (optional)" value={crm.signingSecret ?? ""} onChange={(v) => set({ crm: { ...crm, type: "zapier", signingSecret: v || undefined } })} error={errors["leadCapture.crm.signingSecret"]} help="At least 16 characters. Lets your receiver check that a lead really came from Nemo." />
              </>
            )}
            <Section title="Email a copy to the visitor" help="Off unless you turn it on. The agent asks the visitor first and only sends a copy if they say yes. It contains only what they typed.">
              <Toggle label="Offer visitors an email copy of their details" checked={lc.recap?.enabled === true} onChange={(v) => set({ recap: { ...(lc.recap ?? {}), enabled: v } })} />
              {lc.recap?.enabled && !hasEmail && <p className="error" role="alert">Add the Email detail first: the copy is sent to it.</p>}
              {lc.recap?.enabled && (
                <TextInput label="How to ask for permission" max={300} value={lc.recap.consentText ?? ""} onChange={(v) => set({ recap: { enabled: true, consentText: v || undefined } })} placeholder="Would you like me to email you a copy of the details you shared? You can say no." />
              )}
            </Section>
            {api && agentId && <TestLead api={api} agentId={agentId} saveNow={saveNow} />}
          </>
        )}
      </Section>
    </div>
  );
}

const RESULT: Record<string, string> = { queued: "Waiting…", sending: "Sending…", sent: "Sent", failed: "Failed", not_connected: "Not connected yet", skipped: "Not sent" };

/** Sends a made-up lead through the real destinations and shows what happened to each. */
export function TestLead({ api, agentId, saveNow }: { api: Api; agentId: string; saveNow?: () => Promise<unknown> }) {
  const [state, setState] = React.useState<{ busy: boolean; lead: LeadDetail | null; error: string | null }>({ busy: false, lead: null, error: null });
  const run = async () => {
    setState({ busy: true, lead: null, error: null });
    try {
      await saveNow?.();
      const { id } = await api.sendTestLead(agentId);
      let lead = await api.lead(id);
      for (let i = 0; i < 15 && lead.deliveries.some((d) => d.status === "queued" || d.status === "sending"); i++) {
        await new Promise((r) => setTimeout(r, 2000));
        lead = await api.lead(id);
        setState({ busy: true, lead, error: null });
      }
      setState({ busy: false, lead, error: null });
    } catch (e) {
      setState({ busy: false, lead: null, error: e instanceof ApiError ? e.message : "The test could not run." });
    }
  };
  return (
    <Section title="Try it" help="Sends a made-up lead, marked as a test, to the emails and the webhook above. Your visitors are not involved.">
      <button className="btn secondary" onClick={() => void run()} disabled={state.busy}>
        {state.busy ? "Sending a test lead…" : "Send a test lead"}
      </button>
      {state.error && <p className="error" role="alert">{state.error}</p>}
      {state.lead && (
        <div role="status" aria-label="Test result">
          {state.lead.deliveries.length === 0 && <p>Nothing to send to yet: add a notification email or a webhook.</p>}
          <ul className="rows">
            {state.lead.deliveries.map((d) => (
              <li key={d.id}>
                <strong>{RESULT[d.status] ?? d.status}</strong> · {d.target}
                {d.error && <span className="hint"> — {d.error}</span>}
              </li>
            ))}
          </ul>
          <a href={`#/leads/${state.lead.id}`}>Open this test lead</a>
        </div>
      )}
    </Section>
  );
}

function CustomField({ existing, onAdd }: { existing: string[]; onAdd: (f: LeadField) => void }) {
  const [label, setLabel] = React.useState("");
  const key = slug(label);
  const taken = !!key && existing.includes(key);
  return (
    <div className="row">
      <input aria-label="Custom detail name" placeholder="Custom detail, e.g. Company" value={label} onChange={(e) => setLabel(e.target.value)} />
      <button className="btn secondary" disabled={!key || taken} onClick={() => (onAdd({ key, label: label.trim(), type: "text", required: false }), setLabel(""))}>
        Add detail
      </button>
      {taken && <span className="error">That detail already exists.</span>}
    </div>
  );
}
